/**
 * AppHeader — Top header bar for the authenticated app.
 *
 * Renders a blue-purple gradient bar with:
 * - Breadcrumb navigation on the left (built from current URL)
 * - Global actions on the right (Search, Notifications, Pending, AI Assistant)
 */

import React, { useState, lazy, Suspense } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Search, ChevronRight, Menu } from 'lucide-react';
import NotificationCenter from '../dashboard/NotificationCenter';
import PendingActionsPanel from '../dashboard/PendingActionsPanel';
import GlobalSearch from '../dashboard/GlobalSearch';
import SuperAdminOrgSwitcher from '../superadmin/SuperAdminOrgSwitcher';
import { NAVIGATION } from './AppSidebar';
import { useAuth } from '../../context/AuthContext';
import { usePageContext } from '../../context/PageContext';
import { readAccess } from '../../constants/featureAccess';

const AIAssistant = lazy(() => import('../caleb/AIAssistant'));

const ASSISTANT_READ_ACCESS = readAccess('assistant');

/**
 * Mapping of tab values to their internal sub-view URL params and labels.
 * Used to add a third breadcrumb level (e.g. AI Actions > Campaigns > Active Campaigns).
 */
const VIEW_BREADCRUMBS = {
  campaigns: {
    param: 'campaigns_view',
    defaultView: 'catalog',
    views: {
      catalog: 'Template Catalog',
      instances: 'Active Campaigns',
      analytics: 'Campaign Analytics',
    },
  },
  workflows: {
    param: 'workflows_view',
    defaultView: 'list',
    views: {
      detail: 'Workflow Detail',
      builder: 'Workflow Editor',
    },
  },
  surveys: {
    param: 'surveys_view',
    defaultView: 'templates',
    views: {
      templates: 'Survey Templates',
      analytics: 'Survey Analytics',
    },
  },
};

const AppHeader = ({ user, sidebarCollapsed, onToggleSidebar }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const { pageTitle } = usePageContext();
  const { hasAnyPermission } = useAuth();
  const canUseAssistant = hasAnyPermission(ASSISTANT_READ_ACCESS);

  /**
   * Build breadcrumb trail from the current route and query params.
   * Returns an array of { label, href? } objects.
   */
  const getBreadcrumb = () => {
    const pathname = location.pathname;
    const tab = searchParams.get('tab');

    // Match against main navigation. Tab-bound entries (e.g. Surveys, which
    // shares /app/ai-actions with AI Actions but is owned by ?tab=surveys)
    // win over their pathname-sharing siblings.
    const navItem =
      NAVIGATION.find(
        (item) =>
          item.matchTab &&
          pathname === item.basePath &&
          tab === item.matchTab
      ) ||
      NAVIGATION.find(
        (item) =>
          !item.matchTab &&
          (pathname === item.href || pathname.startsWith(item.href + '/'))
      );

    // Special routes outside main nav
    if (!navItem) {
      if (pathname === '/app/settings') {
        const section = searchParams.get('section');
        const crumbs = [{ label: 'Settings' }];
        if (section && section !== 'profile') {
          const sectionLabel = section
            .split('-')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
          crumbs.push({ label: sectionLabel });
        }
        return crumbs;
      }
      if (pathname === '/app/business-profile') {
        return [
          { label: 'Settings', href: '/app/settings' },
          { label: 'Business Profile' },
        ];
      }
      if (pathname === '/app/refer') return [{ label: 'Refer a Business' }];
      return [{ label: 'Home' }];
    }

    const crumbs = [{ label: navItem.name, href: navItem.href }];

    // Detail pages (e.g. /app/accounts/:id)
    if (
      pathname.startsWith('/app/accounts/') &&
      pathname !== '/app/accounts'
    ) {
      crumbs.push({ label: pageTitle || 'Account Detail' });
      return crumbs;
    }

    // Subtab breadcrumb
    if (navItem.subtabs.length > 0) {
      const activeTab = tab || navItem.defaultTab;
      const subtab = navItem.subtabs.find((s) => s.tabValue === activeTab);
      if (subtab) {
        // Check if this tab has internal sub-views (e.g. catalog/instances/analytics)
        const viewConfig = VIEW_BREADCRUMBS[subtab.tabValue];
        if (viewConfig) {
          const currentView = searchParams.get(viewConfig.param) || viewConfig.defaultView;
          const viewLabel = viewConfig.views[currentView];
          if (viewLabel && currentView !== viewConfig.defaultView) {
            // Non-default view — make the subtab a clickable link
            const subtabHref =
              activeTab === navItem.defaultTab
                ? navItem.href
                : `${navItem.href}?tab=${activeTab}`;
            crumbs.push({ label: subtab.name, href: subtabHref });
            crumbs.push({ label: viewLabel });
          } else {
            crumbs.push({ label: subtab.name });
          }
        } else {
          crumbs.push({ label: subtab.name });
        }
      }
    } else if (navItem.matchTab) {
      // Tab-bound top-level item (e.g. Surveys). The tab itself is the page,
      // so we don't push a subtab crumb — but we still surface the page's
      // internal view (templates / analytics) when one is active. The page
      // crumb already carries `href`, so the renderer auto-makes it clickable
      // once a second crumb is pushed.
      const viewConfig = VIEW_BREADCRUMBS[navItem.matchTab];
      if (viewConfig) {
        const currentView = searchParams.get(viewConfig.param) || viewConfig.defaultView;
        const viewLabel = viewConfig.views[currentView];
        if (viewLabel && currentView !== viewConfig.defaultView) {
          crumbs.push({ label: viewLabel });
        }
      }
    }

    return crumbs;
  };

  const breadcrumbs = getBreadcrumb();

  return (
    <>
      <header className="bg-brand-gradient h-12 flex items-center px-6 flex-shrink-0 z-30">
        <div className="flex items-center justify-between w-full">
          {/* Left: Mobile sidebar toggle + Breadcrumb */}
          <div className="flex items-center min-w-0">
            <button
              type="button"
              onClick={onToggleSidebar}
              className="lg:hidden mr-3 p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" />
            </button>

            <nav className="flex items-center space-x-1.5 min-w-0" aria-label="Breadcrumb">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <ChevronRight className="h-4 w-4 text-white/40 flex-shrink-0" />
                  )}
                  {crumb.href && index < breadcrumbs.length - 1 ? (
                    <button
                      onClick={() => navigate(crumb.href)}
                      className="text-white/70 hover:text-white text-sm font-medium transition-colors whitespace-nowrap"
                    >
                      {crumb.label}
                    </button>
                  ) : (
                    <span className="text-white text-sm font-semibold whitespace-nowrap truncate">
                      {crumb.label}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>

          {/* Right: Global actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Search */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSearch(true)}
              className="text-white/80 hover:text-white hover:bg-white/10 hidden md:flex items-center space-x-2"
              aria-label="Search (⌘K)"
            >
              <Search className="h-4 w-4" />
              <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-2xs text-white/60">
                ⌘K
              </kbd>
            </Button>

            {/* AI Assistant */}
            {canUseAssistant && (
              <Suspense fallback={null}>
                <AIAssistant
                  user={user}
                  triggerAvatarVariant="light"
                  triggerClassName="text-white/80 hover:bg-white/10 hover:text-white"
                />
              </Suspense>
            )}

            {/* Super Admin Org Switcher */}
            <SuperAdminOrgSwitcher user={user} />

            {/* Notification Center */}
            <NotificationCenter user={user} triggerClassName="text-white/80 hover:text-white hover:bg-white/10" />

            {/* Pending Actions */}
            <PendingActionsPanel user={user} triggerClassName="text-white/80 hover:text-white hover:bg-white/10" />

          </div>
        </div>
      </header>

      {/* Global Search Modal */}
      {showSearch && (
        <GlobalSearch isOpen={showSearch} setIsOpen={setShowSearch} />
      )}
    </>
  );
};

export default AppHeader;
